/**
 * Shared Keystone attempt tracker — the single gate between `parseStreamFrame`
 * and a consumer's render logic for one stream connection.
 *
 * Wire contract (keystone/src/services/queue/workers/conversation-turn.ts:150-250):
 * Keystone may retry a turn. Every retry-aware frame carries `turnId` + `attempt`.
 * A non-final attempt CAN write `assistant`/`error` (keystone run-ask-turn.ts:506-566, :1683) and then fail in `finish` or die (conversation-turn.ts:202-248); the next attempt's marker retracts them. `assistant`/`error` end an attempt, not the turn; `turn_done` (best-effort, may never arrive) ends the turn.
 * The `turn_superseded` marker is the first append of a retry and
 * carries the NEW attribution with `supersededAttempt = attempt - 1`.
 *
 * Constraints on this file: ZERO runtime imports (type-only, logger-free graph, so it
 * type-checks under tsconfig.tests.json, tsconfig.miniapps.json and web/app-home), and
 * ES2020-lib safe (Set/Map only — no `.at()`, `findLast` or other ES2022+ APIs).
 */
import type { StreamMessage, TurnSupersededMessage } from '../types/stream-protocol';

/** Every frame except the marker. `admit` never hands a marker back to render code. */
export type RenderableFrame = Exclude<StreamMessage, TurnSupersededMessage>;

export type Admission =
  | { readonly kind: 'render'; readonly frame: RenderableFrame }
  | { readonly kind: 'skip'; readonly reason: 'duplicate' | 'superseded' | 'repeat-marker' }
  | { readonly kind: 'supersede'; readonly removedIds: ReadonlySet<string> };

export interface AttemptTracker {
  /**
   * Classify one parsed frame. Invariants:
   *  - Dedupe by `messageId` for every frame except `assistant_delta` (deltas repeat
   *    their parent's id and are never deduped here).
   *  - EVERY attributed non-user frame is indexed under its attempt (a delta under its
   *    `parentMessageId`), not only rendered ones, so `removedIds` is a superset of the
   *    ids a consumer rendered for that attempt.
   *  - A marker supersedes (`turnId`, `supersededAttempt`) once: it forgets those ids from
   *    the dedupe set (so a stable `messageId` reused by the retry renders again) and
   *    returns them as `removedIds`. A repeat marker returns `skip('repeat-marker')`.
   *  - Later non-user frames of a superseded attempt return `skip('superseded')`.
   *  - `user` frames are always exempt from hiding and never appear in `removedIds`.
   *  - `retryPending` is true from a supersede until the next admitted `assistant`,
   *    `error` or `turn_done` frame; deltas, status and metadata frames leave it unchanged.
   */
  admit(frame: StreamMessage): Admission;
  /** Optimistic local send: the server echo of this id then skips as `duplicate`. */
  markSeen(messageId: string): void;
  /** True from a `supersede` until the next admitted assistant/error/turn_done frame. */
  readonly retryPending: boolean;
}

export function createAttemptTracker(): AttemptTracker {
  const seen = new Set<string>();
  const byAttempt = new Map<string, Set<string>>();
  const superseded = new Set<string>();
  let retryPending = false;

  return {
    admit(frame) {
      if (frame.type === 'turn_superseded') {
        const k = attemptKey(frame.turnId, frame.supersededAttempt);
        if (superseded.has(k)) return { kind: 'skip', reason: 'repeat-marker' };
        superseded.add(k);
        const ids = byAttempt.get(k) ?? new Set<string>();
        for (const id of ids) seen.delete(id);
        byAttempt.delete(k);
        retryPending = true;
        return { kind: 'supersede', removedIds: ids };
      }

      const k = frame.turnId !== undefined && frame.attempt !== undefined
        ? attemptKey(frame.turnId, frame.attempt)
        : null;
      const tracked = k !== null && frame.type !== 'user';
      if (tracked && superseded.has(k)) return { kind: 'skip', reason: 'superseded' };

      const id = frame.type === 'assistant_delta' ? frame.parentMessageId : frame.messageId;
      if (frame.type !== 'assistant_delta') {
        if (seen.has(id)) return { kind: 'skip', reason: 'duplicate' };
        seen.add(id);
      }
      if (tracked) {
        const ids = byAttempt.get(k) ?? new Set<string>();
        ids.add(id);
        byAttempt.set(k, ids);
      }
      if (frame.type === 'assistant' || frame.type === 'error' || frame.type === 'turn_done') retryPending = false;
      return { kind: 'render', frame };
    },
    markSeen(messageId) {
      seen.add(messageId);
    },
    get retryPending() {
      return retryPending;
    },
  };
}

/** Module-internal `${turnId}#${attempt}` key; matches Keystone's attemptKey. */
function attemptKey(turnId: string, attempt: number): string {
  return `${turnId}#${attempt}`;
}
